import { Fragment, useState } from 'react'
import { CheckoutContext, useCheckoutContext } from '../contexts/checkout.context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCircleCheck, faCircleXmark, faCoins, faWallet } from '@fortawesome/free-solid-svg-icons'
import { PaymentSelect } from './PaymentSelect'
import type { PriceType } from '../types'
import { WalletPrevicew, WalletSelect } from './WalletSelect'
import { Wallet } from '~/features/wallet/types'
import { Price } from './Price'
import { Button } from './ui/Button'
import { useDialogContext } from '../contexts/dialog.context'
import { twMerge } from 'tailwind-merge'
import { useStoreContext } from '../contexts/store.context'
import { useStoreItemContext } from '../contexts/store-item.context'
import { useStoreGate } from '../hooks/useStoreGate'
import { PURCHASE_FAILED } from '../api/store'

interface CheckoutProps {
    children: React.ReactNode
}

export const Checkout = ({ children }: CheckoutProps) => {
    const [step, setStep] = useState(0)
    const [selectedPrice, setSelectedPrice] = useState<PriceType>(null)
    const [selectedWallet, setSelectedWallet] = useState<Wallet>(undefined)

    const contextValue = {
        step,
        setStep,
        selectedPrice,
        setSelectedPrice,
        selectedWallet,
        setSelectedWallet,
    }

    return <CheckoutContext.Provider value={contextValue}>{children}</CheckoutContext.Provider>
}

const CheckoutHeader = () => {
    const { step, setStep } = useCheckoutContext()

    // 3 is the confirmation and 4 the failure; neither has anything to go back to.
    const canGoBack = step > 0 && step < 3
    const text = ['Select currency', 'Select wallet', 'Summary', 'Confirmed', 'Not completed']

    return (
        <div className='inline-flex gap-2 items-center'>
            {canGoBack && (
                <button className='hover:scale-110' onClick={() => setStep((s) => s - 1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
            )}
            <span className='uppercase text-xs font-bold'>{text[step]}</span>
        </div>
    )
}

const CheckoutSummary = () => {
    const { selectedPrice, selectedWallet, setStep } = useCheckoutContext()
    const { item } = useStoreItemContext()
    const { api } = useStoreContext()
    const { setState, setError } = useStoreItemContext()
    const { blockedReason } = useStoreGate()
    const [loading, setLoading] = useState(false)

    const handlePurchase = async () => {
        if (!selectedPrice || !selectedWallet || blockedReason) return
        setLoading(true)
        try {
            await api.createOrder({ listing_price: selectedPrice.uuid, delivery_address: selectedWallet.pubkey })
            setStep((s) => s + 1)
            setState('purchased')
        } catch (e) {
            // Step 4 rather than step 3. A refused order is not a purchase, and the shopper has to
            // see that before anything else: this branch is what used to fall through to the
            // confirmation screen and thank somebody for an order that was never created.
            setError(e instanceof Error ? e.message : PURCHASE_FAILED)
            setStep(4)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='text-2xs uppercase text-white/50'>Currency:</div>
            <Price price={selectedPrice} />
            <div className='text-2xs uppercase text-white/50'>Wallet:</div>
            <WalletPrevicew pubkey={selectedWallet.pubkey} />
            {blockedReason && <div className='text-2xs uppercase text-center text-amber-400/80'>{blockedReason}</div>}
            <Button onClick={handlePurchase} spinner={loading} disabled={!!blockedReason} title={blockedReason}>
                BUY NOW
            </Button>
        </div>
    )
}

const CheckoutConfirmed = () => {
    const { close } = useDialogContext()

    return (
        <div className='flex flex-col text-center gap-4'>
            <FontAwesomeIcon icon={faCircleCheck} className='text-green-500 text-3xl' />
            <div className='text-lg font-bold'>Thank you for your purchase!</div>
            <div className='text-2xs uppercase'>Your item will be delivered to your wallet shortly.</div>
            <Button onClick={close}>BACK TO STORE</Button>
        </div>
    )
}

/**
 * The refusal screen. Written when the checkout was, and never rendered until now -- nothing set an
 * error, because nothing could detect one. `faCircleXmark`, not the confirmation's tick in red: at
 * the size these render, a red tick still reads as a tick.
 */
const CheckoutError = () => {
    const { close } = useDialogContext()
    const { error } = useStoreItemContext()

    return (
        <div className='flex flex-col text-center gap-4'>
            <FontAwesomeIcon icon={faCircleXmark} className='text-red-500 text-3xl' />
            <div className='text-lg font-bold'>Purchase failed</div>
            <div className='text-2xs uppercase'>{error || PURCHASE_FAILED}</div>
            <Button onClick={close}>BACK TO STORE</Button>
        </div>
    )
}

const CheckoutProgress = () => {
    const { step } = useCheckoutContext()
    const icons = [faCoins, faWallet, faCircleCheck]

    // Step 4 is the failure screen, not a fourth step forward. Reading `i < step` there would tick
    // all three green behind a message saying the purchase failed.
    const reached = step === 4 ? 2 : step

    return (
        <div className='flex flex-row items-center justify-center gap-4 p-4 w-3/4 mx-auto'>
            {icons.map((icon, i) => {
                const stepPassed = i < reached
                return (
                    <Fragment key={i}>
                        <FontAwesomeIcon
                            icon={icon}
                            className={twMerge(stepPassed ? 'text-green-500' : 'text-white/20')}
                        />
                        {i < icons.length - 1 && <hr className='border-white/20 grow'></hr>}
                    </Fragment>
                )
            })}
        </div>
    )
}

const CheckoutForm = () => {
    const { step } = useCheckoutContext()
    const steps = [<PaymentSelect />, <WalletSelect />, <CheckoutSummary />, <CheckoutConfirmed />, <CheckoutError />]

    return steps[step]
}

Checkout.Progress = CheckoutProgress
Checkout.Header = CheckoutHeader
Checkout.Form = CheckoutForm
